import { useEffect, useMemo, useRef, useState } from 'react';
import { nodes as staticNodes, createNodeRegistry } from './nodes';
import { mergeNodeDefinitions } from './nodeSync';
import fetchXacThuc from '../auth/fetchXacThuc';

/**
 * useFlowNodes — hỏi máy chủ "flow này chạy được những node nào", rồi trộn với khai báo ở client.
 *
 *   GET /api/:id_app/flow/nodes?full=1  ->  { ok, count, nodes:[ spec đầy đủ ] }
 *
 * Kết quả: danh sách node ĐÚNG với máy chủ đang dùng — node của gói chưa cài KHÔNG hiện ra nữa,
 * node máy chủ mới thêm thì hiện ngay dù client chưa cập nhật (xem nodeSync.js).
 *
 * An toàn khi hỏi hỏng: giữ nguyên danh sách tĩnh của client. Người dùng vẫn soạn được flow như trước
 * — thà hiện thừa vài node còn hơn khoá trắng màn hình.
 *
 * Danh sách node chỉ đổi khi máy chủ khởi động lại nên nhớ theo (apiUrl, idApp) cho cả phiên làm việc.
 */

const _cache = new Map();   // `${apiUrl}|${idApp}` -> Promise<{nodes, missing}|null>

export function clearFlowNodesCache() { _cache.clear(); }

export async function fetchServerNodes({ apiUrl, idApp, accessToken, trustKey, apis }) {
  if (!apiUrl || !idApp) return null;
  const key = `${apiUrl}|${idApp}`;
  if (_cache.has(key)) return _cache.get(key);

  const p = (async () => {
    const url = `${apiUrl}/api/${idApp}/flow/nodes?full=1`
      + `&access_token=${encodeURIComponent(accessToken || '')}`
      + (trustKey ? `&trustkey=${encodeURIComponent(trustKey)}` : '');
    // fetchXacThuc: token hết hạn thì tự làm mới + chạy lại; không cứu được thì mở form đăng nhập.
    const res = await fetchXacThuc(apis, url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const list = Array.isArray(body) ? body : (body && body.nodes);
    if (!Array.isArray(list)) throw new Error('phản hồi không có danh sách node');
    // `missing`: node có tên nhưng gói cung cấp chưa cài -> để báo ĐÚNG tên gói cần cài.
    const missing = {};
    for (const m of (body && body.missing) || []) if (m && m.type) missing[m.type] = m.pkg || '';
    return { nodes: list, missing };
  })().catch((e) => {
    // Xoá khỏi bộ nhớ để lần mở sau còn thử lại (có thể chỉ là lỗi mạng nhất thời).
    _cache.delete(key);
    const loi = (e && e.message) || String(e);
    console.warn('[flow] không lấy được danh sách node của máy chủ:', loi);
    // TRẢ LÝ DO VỀ, KHÔNG CHỈ `null`. Bản trước chỉ ghi console rồi lặng lẽ lùi về danh sách tĩnh:
    // node của gói biến khỏi thanh bên trái và vẽ thành khung trắng, mà trên màn hình KHÔNG có
    // một chữ nào nói vì sao. Đã có đúng lỗi này thật (endpoint trả 404) và nó nằm im cho tới khi
    // người dùng hỏi. `nodes: null` vẫn giữ hành vi lùi về danh sách tĩnh như cũ.
    return { nodes: null, missing: {}, loi };
  });

  _cache.set(key, p);
  return p;
}

export default function useFlowNodes({ apiUrl, idApp, accessToken, trustKey, apis } = {}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(!!(apiUrl && idApp));
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    if (!apiUrl || !idApp) { setLoading(false); return undefined; }
    setLoading(true);
    fetchServerNodes({ apiUrl, idApp, accessToken, trustKey, apis }).then((rs) => {
      if (!alive.current) return;
      setPayload(rs);
      setLoading(false);
    });
    return () => { alive.current = false; };
  }, [apiUrl, idApp, accessToken, trustKey]);

  // Trộn + dựng map component. useMemo là BẮT BUỘC: ReactFlow so sánh `nodeTypes` theo tham chiếu,
  // đổi tham chiếu mỗi lần render sẽ vẽ lại toàn bộ sơ đồ (và cảnh báo trên console).
  const merged = useMemo(() => mergeNodeDefinitions(staticNodes, payload && payload.nodes), [payload]);
  // Map component dựng từ `all` (kể cả node máy chủ không có) để flow ĐÃ LƯU vẫn vẽ và sửa được.
  const registry = useMemo(() => createNodeRegistry(merged.all), [merged.all]);

  return {
    nodes: merged.nodes,          // chỉ node ĐƯỢC PHÉP thêm mới -> thanh bên trái, kiểm tra hợp lệ
    nodeTypes: registry.nodeTypes,
    nodeTypeMetadata: registry.nodeTypeMetadata,
    hidden: merged.hidden,        // node client có mà máy chủ không -> ẩn khỏi thanh bên trái
    missingPackages: (payload && payload.missing) || {},   // { type: 'tên gói npm cần cài' }
    serverError: (payload && payload.loi) || '',            // hỏi máy chủ hỏng -> màn hình phải NÓI RA
    dynamic: merged.dynamic,      // node máy chủ có mà client chưa khai báo -> đang dùng node động
    fromServer: merged.fromServer,
    loading,
  };
}
